import { Controller } from "@hotwired/stimulus"

/**
 * Guards a form against losing unsaved edits (booking form parity with the
 * React booking form's useUnsavedChangesWarning — CONEK-4417).
 *
 * Marks the form dirty on the first input/change, then intercepts navigation:
 *   - Turbo Drive visits (cancel link, calendar nav) → turbo:before-visit
 *   - full-page unload / tab close → beforeunload
 * Submitting the form clears the guard so a save never prompts.
 *
 * Markup contract (booking_form_component.html.erb):
 *
 *   <form data-controller="pipeline--unsaved-changes"
 *         data-pipeline--unsaved-changes-message-value="…"
 *         data-action="submit->pipeline--unsaved-changes#allowSubmit
 *                      input->pipeline--unsaved-changes#markDirty
 *                      change->pipeline--unsaved-changes#markDirty">
 *     <div data-pipeline--unsaved-changes-target="banner">…
 *       <button data-pipeline--unsaved-changes-target="keepEditing">…
 *
 * The banner is the form's first child, so on a vetoed modal dismiss it is
 * scrolled into view and "Keep editing" is focused (CONEK-7677) — otherwise a
 * user scrolled down to Cancel sees nothing happen.
 */
export default class extends Controller {
  static targets = ["banner", "keepEditing"]
  static classes = ["open", "closed"]
  static values = {
    message: {
      type: String,
      default: "You have unsaved changes. Leave without saving?",
    },
    dirty: { type: Boolean, default: false },
  }

  connect() {
    this.dirty = this.dirtyValue
    this.submitting = false
    // A cached snapshot (Turbo Back) could restore the banner mid-open; start
    // it collapsed so it only ever appears on a live dismiss.
    this._hideBanner()
    this._onBeforeUnload = this._onBeforeUnload.bind(this)
    this._onBeforeVisit = this._onBeforeVisit.bind(this)
    this._onSubmitEnd = this._onSubmitEnd.bind(this)
    window.addEventListener("beforeunload", this._onBeforeUnload)
    document.addEventListener("turbo:before-visit", this._onBeforeVisit)
    this.element.addEventListener("turbo:submit-end", this._onSubmitEnd)
  }

  disconnect() {
    if (this._revealFrame) cancelAnimationFrame(this._revealFrame)
    this._revealFrame = null
    this._returnFocusTo = null
    window.removeEventListener("beforeunload", this._onBeforeUnload)
    document.removeEventListener("turbo:before-visit", this._onBeforeVisit)
    this.element.removeEventListener("turbo:submit-end", this._onSubmitEnd)
  }

  markDirty() {
    this.dirty = true
  }

  // Submitting is a deliberate save — never guard it (Turbo issues its own
  // visit after the response, which must not be blocked).
  allowSubmit() {
    this.submitting = true
  }

  // Re-arm the guard if the submission did NOT succeed. On the desktop modal a
  // 422 validation failure updates #booking_form_errors in place, so the form
  // (and this controller instance) stays mounted with submitting=true — without
  // this, every later Turbo visit would silently bypass the unsaved-changes
  // prompt and discard the user's edits. A successful submit navigates away and
  // disconnects the controller, so no reset is needed there.
  _onSubmitEnd(event) {
    if (!event.detail?.success) this.submitting = false
  }

  // A modal dismiss (X, Cancel, or backdrop click) is about to close the form.
  // The desktop booking modal closes via the DOM — not a Turbo visit — so it
  // isn't covered by _onBeforeVisit; without this, dismissing the modal would
  // silently discard edits. Wired via `modal:before-close@document` (the event
  // bubbles from the <dialog>, an ancestor of this form). With pending edits we
  // veto the close and surface the inline warning banner (v1 parity) instead of
  // a native window.confirm; the user then resolves it via Discard / Keep
  // editing. The banner is then brought into view (CONEK-7677).
  confirmClose(event) {
    // Only guard the dialog that actually wraps this form.
    if (!event.target.contains(this.element)) return
    if (!this._shouldGuard()) return
    event.preventDefault()
    this._rememberFocus()
    this._showBanner()
    this._revealBanner()
  }

  // Banner "Keep editing" — dismiss the warning and leave the modal open; the
  // form stays dirty so a later dismiss re-prompts. The reveal moved focus to
  // this button, which collapses with the banner, so hand focus back to where
  // the user was before the dismiss (CONEK-7677).
  keepEditing() {
    this._hideBanner()
    const target = this._returnFocusTo
    this._returnFocusTo = null
    if (target?.isConnected) target.focus({ preventScroll: true })
  }

  // Banner "Discard" — drop the edits and close the modal for real. Clearing
  // `dirty` first means the re-issued close() is no longer vetoed by
  // confirmClose, so the dialog actually closes.
  discard() {
    this.dirty = false
    // No focus restore here: the modal's close() returns focus to its opener.
    this._returnFocusTo = null
    this._hideBanner()
    const dialog = this.element.closest("dialog")
    if (!dialog) return
    const modal = this.application.getControllerForElementAndIdentifier(
      dialog,
      "ui--modal",
    )
    if (modal) {
      modal.close()
    } else {
      // Defensive fallback if the modal controller isn't mounted.
      dialog.removeAttribute("open")
      dialog.setAttribute("data-ui--modal-open-value", "false")
    }
  }

  // Remember what had focus before the reveal moves it to "Keep editing". A
  // repeated dismiss finds focus already on that button, so it is skipped to
  // keep the original element.
  _rememberFocus() {
    const active = document.activeElement
    if (!active || active === document.body) return
    if (this.hasKeepEditingTarget && active === this.keepEditingTarget) return
    const scope = this.element.closest("dialog") || this.element
    if (scope.contains(active)) this._returnFocusTo = active
  }

  _showBanner() {
    if (!this.hasBannerTarget) return
    if (this.hasClosedClass)
      this.bannerTarget.classList.remove(...this.closedClasses)
    if (this.hasOpenClass) this.bannerTarget.classList.add(...this.openClasses)
  }

  // Scroll the banner to the top of the modal's scroller and focus "Keep
  // editing", one frame after it starts expanding. Focus skips its own scroll
  // so it doesn't fight the smooth scroll; reduced motion jumps instead.
  _revealBanner() {
    if (!this.hasBannerTarget) return
    if (this._revealFrame) cancelAnimationFrame(this._revealFrame)
    this._revealFrame = requestAnimationFrame(() => {
      this._revealFrame = null
      const banner = this.bannerTarget
      if (typeof banner.scrollIntoView === "function") {
        const reduce = window.matchMedia?.(
          "(prefers-reduced-motion: reduce)",
        )?.matches
        banner.scrollIntoView({
          block: "start",
          behavior: reduce ? "auto" : "smooth",
        })
      }
      if (this.hasKeepEditingTarget)
        this.keepEditingTarget.focus({ preventScroll: true })
    })
  }

  _hideBanner() {
    if (!this.hasBannerTarget) return
    if (this.hasOpenClass)
      this.bannerTarget.classList.remove(...this.openClasses)
    if (this.hasClosedClass)
      this.bannerTarget.classList.add(...this.closedClasses)
  }

  _shouldGuard() {
    return this.dirty && !this.submitting
  }

  _onBeforeUnload(event) {
    if (!this._shouldGuard()) return
    event.preventDefault()
    // Chrome requires returnValue to be set for the native prompt to show.
    event.returnValue = ""
    return ""
  }

  _onBeforeVisit(event) {
    if (!this._shouldGuard()) return
    if (!window.confirm(this.messageValue)) {
      event.preventDefault()
    }
  }
}
